import React, { useState, useEffect } from 'react';
import { Database, HardDrive, RefreshCw, Save, Download, Trash2, X, Plus, Edit2, ChevronDown, ChevronUp, FileText, Eye, EyeOff, Lock, Calendar, CreditCard, Upload, User, CheckCircle, AlertTriangle, Phone, Mail, Shield, Briefcase, Cake, Heart, Users } from 'lucide-react';
import { UserManagementPanel } from './UsersManager';
import { HealthCheckPanel } from './HealthManager';
import AdminErrorBoundary from './AdminErrorBoundary';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StorageCapacityPanel = ({ token }) => {
  const [capacity, setCapacity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchCapacity();
  }, []);

  const fetchCapacity = async () => {
    try {
      const response = await axios.get(`${API}/admin/storage/capacity`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCapacity(response.data);
    } catch (error) {
      console.error('Error fetching capacity:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCapacity = async () => {
    setSaving(true);
    try {
      const params = new URLSearchParams({
        daily_itinerary_limit: capacity.daily_itinerary_limit,
        cleanup_after_days: capacity.cleanup_after_days,
        compress_after_days: capacity.compress_after_days,
        max_storage_gb: capacity.max_storage_gb,
        auto_cleanup_enabled: capacity.auto_cleanup_enabled
      });
      
      const response = await axios.put(`${API}/admin/storage/capacity?${params}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCapacity(response.data.config);
      setMessage({ type: 'success', text: 'Storage capacity settings saved' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleCleanupOldPDFs = async () => {
    if (!window.confirm('This will permanently delete PDF files older than the configured days. Continue?')) return;
    
    setCleaning(true);
    try {
      const response = await axios.post(`${API}/admin/storage/cleanup-old-pdfs`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: `Cleanup complete: ${response.data.deleted_files} files deleted, ${response.data.freed_mb}MB freed` });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to cleanup old PDFs' });
    } finally {
      setCleaning(false);
    }
  };

  if (loading) return <div className="settings-card"><p>Loading storage capacity...</p></div>;

  return (
    <div className="settings-card storage-capacity-section">
      <h3><Briefcase size={18} /> Storage Capacity (500 itineraries/day)</h3>
      
      {message.text && (
        <div className={`settings-message ${message.type}`}>{message.text}</div>
      )}

      {capacity && (
        <>
          <div className="capacity-stats">
            <div className="capacity-stat">
              <span className="stat-label">Daily Limit</span>
              <span className="stat-value">{capacity.daily_itinerary_limit} itineraries</span>
            </div>
            <div className="capacity-stat">
              <span className="stat-label">Est. Daily Storage</span>
              <span className="stat-value">{capacity.daily_storage_mb} MB</span>
            </div>
            <div className="capacity-stat">
              <span className="stat-label">Est. Monthly Storage</span>
              <span className="stat-value">{capacity.monthly_storage_gb} GB</span>
            </div>
            <div className="capacity-stat">
              <span className="stat-label">Max Storage Limit</span>
              <span className="stat-value">{capacity.max_storage_gb} GB</span>
            </div>
          </div>

          <div className="capacity-settings-form">
            <div className="field">
              <label>Daily Itinerary Limit</label>
              <Input 
                type="number" 
                min="1" 
                value={capacity.daily_itinerary_limit}
                onChange={(e) => setCapacity({...capacity, daily_itinerary_limit: parseInt(e.target.value)})}
              />
            </div>
            <div className="field">
              <label>Delete PDFs after (days)</label>
              <Input 
                type="number" 
                min="7" 
                value={capacity.cleanup_after_days}
                onChange={(e) => setCapacity({...capacity, cleanup_after_days: parseInt(e.target.value)})}
              />
            </div>
            <div className="field">
              <label>Max Storage Limit (GB)</label>
              <Input 
                type="number" 
                min="10" 
                value={capacity.max_storage_gb}
                onChange={(e) => setCapacity({...capacity, max_storage_gb: parseInt(e.target.value)})}
              />
            </div>
            <div className="field checkbox-field">
              <input 
                type="checkbox"
                id="auto-cleanup"
                checked={capacity.auto_cleanup_enabled}
                onChange={(e) => setCapacity({...capacity, auto_cleanup_enabled: e.target.checked})}
              />
              <label htmlFor="auto-cleanup">Enable auto-cleanup of old PDFs</label>
            </div>
          </div>

          <div className="capacity-actions">
            <Button onClick={handleSaveCapacity} disabled={saving} className="save-btn">
              <Save size={16} /> {saving ? 'Saving...' : 'Save Settings'}
            </Button>
            <Button onClick={handleCleanupOldPDFs} disabled={cleaning} className="cleanup-btn">
              <Trash2 size={16} /> {cleaning ? 'Cleaning...' : 'Cleanup Old PDFs Now'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};


// Website Health Check Panel Component

const FormFieldEditor = ({ token }) => {
  const [formConfig, setFormConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddField, setShowAddField] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [newField, setNewField] = useState({
    id: '',
    label: '',
    type: 'text',
    placeholder: '',
    required: false,
    section: 'preferences',
    options: []
  });

  useEffect(() => {
    fetchFormConfig();
  }, []);

  const fetchFormConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/design-journey/form-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormConfig(response.data);
    } catch (error) {
      console.error('Error fetching form config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddField = async (e) => {
    e.preventDefault();
    try {
      const fieldData = {
        ...newField,
        id: newField.id.toLowerCase().replace(/\s+/g, '_'),
        order: formConfig?.fields?.length || 0
      };
      
      if (newField.type === 'select' && typeof newField.options === 'string') {
        fieldData.options = newField.options.split(',').map(o => o.trim()).filter(o => o);
      }
      
      await axios.post(`${API}/admin/design-journey/form-config/field`, fieldData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'Field added successfully' });
      setShowAddField(false);
      setNewField({ id: '', label: '', type: 'text', placeholder: '', required: false, section: 'preferences', options: [] });
      fetchFormConfig();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to add field' });
    }
  };

  const handleDeleteField = async (fieldId) => {
    if (!window.confirm('Are you sure you want to delete this field?')) return;
    
    try {
      await axios.delete(`${API}/admin/design-journey/form-config/field/${fieldId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'Field deleted successfully' });
      fetchFormConfig();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to delete field' });
    }
  };

  const protectedFields = ['first_name', 'last_name', 'email', 'phone'];

  if (loading) return <p>Loading form configuration...</p>;

  return (
    <div className="form-field-editor">
      <div className="section-header">
        <h3><Edit2 size={18} /> Itinerary Form Editor</h3>
        <Button onClick={() => setShowAddField(!showAddField)} className="add-field-btn">
          <Plus size={16} /> Add Field
        </Button>
      </div>

      {message.text && (
        <div className={`settings-message ${message.type}`}>{message.text}</div>
      )}

      {showAddField && (
        <form onSubmit={handleAddField} className="add-field-form">
          <div className="form-row">
            <Input 
              placeholder="Field ID (e.g., company_name)" 
              value={newField.id}
              onChange={(e) => setNewField({...newField, id: e.target.value})}
              required
            />
            <Input 
              placeholder="Label" 
              value={newField.label}
              onChange={(e) => setNewField({...newField, label: e.target.value})}
              required
            />
          </div>
          <div className="form-row">
            <Select value={newField.type} onValueChange={(val) => setNewField({...newField, type: val})}>
              <SelectTrigger>
                <SelectValue placeholder="Field Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="text">Text</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="phone">Phone</SelectItem>
                <SelectItem value="number">Number</SelectItem>
                <SelectItem value="date">Date</SelectItem>
                <SelectItem value="select">Dropdown</SelectItem>
                <SelectItem value="textarea">Textarea</SelectItem>
              </SelectContent>
            </Select>
            <Select value={newField.section} onValueChange={(val) => setNewField({...newField, section: val})}>
              <SelectTrigger>
                <SelectValue placeholder="Section" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="personal">Personal Info</SelectItem>
                <SelectItem value="trip_details">Trip Details</SelectItem>
                <SelectItem value="preferences">Preferences</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="form-row">
            <Input 
              placeholder="Placeholder text" 
              value={newField.placeholder}
              onChange={(e) => setNewField({...newField, placeholder: e.target.value})}
            />
            <label className="checkbox-inline">
              <input 
                type="checkbox"
                checked={newField.required}
                onChange={(e) => setNewField({...newField, required: e.target.checked})}
              />
              Required
            </label>
          </div>
          {newField.type === 'select' && (
            <div className="form-row">
              <Input 
                placeholder="Options (comma separated, e.g., Option 1, Option 2, Option 3)" 
                value={typeof newField.options === 'string' ? newField.options : newField.options?.join(', ')}
                onChange={(e) => setNewField({...newField, options: e.target.value})}
              />
            </div>
          )}
          <div className="form-actions">
            <Button type="submit" className="save-btn"><Save size={14} /> Add Field</Button>
            <Button type="button" onClick={() => setShowAddField(false)} className="cancel-btn"><X size={14} /> Cancel</Button>
          </div>
        </form>
      )}

      <div className="form-fields-list">
        {formConfig?.fields?.sort((a, b) => a.order - b.order).map((field) => (
          <div key={field.id} className={`form-field-item ${protectedFields.includes(field.id) ? 'protected' : ''}`}>
            <div className="field-info">
              <span className="field-label">{field.label}</span>
              <span className="field-type">{field.type}</span>
              <span className={`field-section ${field.section}`}>{field.section}</span>
              {field.required && <span className="field-required">Required</span>}
            </div>
            <div className="field-actions">
              {!protectedFields.includes(field.id) && (
                <Button onClick={() => handleDeleteField(field.id)} className="delete-btn-sm" title="Delete field">
                  <Trash2 size={14} />
                </Button>
              )}
              {protectedFields.includes(field.id) && (
                <span className="protected-badge">System Field</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


const AdminProfilePanel = ({ token, currentUser }) => {
  const [accountInfo, setAccountInfo] = useState({ name: '', email: '', phone: '' });
  const [profile, setProfile] = useState({ birthday: '', anniversary: '', aadhar_number: '', pan_number: '', passport_number: '', passport_expiry: '' });
  const [passwordData, setPasswordData] = useState({ current: '', newPass: '', confirm: '' });
  const [emailData, setEmailData] = useState({ newEmail: '', password: '' });
  const [editingAccount, setEditingAccount] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [changingEmail, setChangingEmail] = useState(false);
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accountError, setAccountError] = useState('');
  const [accountSuccess, setAccountSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [documents, setDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const [uploadError, setUploadError] = useState('');

  const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => { fetchAdminProfile(); }, []);

  const fetchAdminProfile = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const resp = await axios.get(`${API_BASE}/admin/me`, { headers });
      if (resp.data) {
        setAccountInfo({ name: resp.data.name || '', email: resp.data.email || '', phone: resp.data.phone || '' });
        if (resp.data.profile) {
          setProfile({
            birthday: resp.data.profile.birthday || '', anniversary: resp.data.profile.anniversary || '',
            aadhar_number: resp.data.profile.aadhar_number || '', pan_number: resp.data.profile.pan_number || '',
            passport_number: resp.data.profile.passport_number || '', passport_expiry: resp.data.profile.passport_expiry || ''
          });
          setDocuments(resp.data.profile.documents || []);
        }
      }
    } catch (e) { console.error('Failed to fetch profile', e); }
  };

  const handleDocUpload = async (e, docType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!allowed.includes(file.type)) { setUploadError('Only JPG, PNG, or PDF allowed'); setTimeout(() => setUploadError(''), 3000); return; }
    if (file.size > 5 * 1024 * 1024) { setUploadError('File must be under 5MB'); setTimeout(() => setUploadError(''), 3000); return; }
    setUploadingDoc(true); setUploadMessage(''); setUploadError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('doc_type', docType);
      const resp = await axios.post(`${API_BASE}/client/upload-document`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      if (resp.data.success) {
        setDocuments(prev => [...prev, resp.data.document]);
        setUploadMessage(`"${file.name}" uploaded as ${docType}`);
        setTimeout(() => setUploadMessage(''), 4000);
      }
    } catch (err) {
      setUploadError(err.response?.data?.detail || 'Upload failed');
      setTimeout(() => setUploadError(''), 4000);
    } finally { setUploadingDoc(false); }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API_BASE}/client/document/${docId}`, { headers: { Authorization: `Bearer ${token}` } });
      setDocuments(prev => prev.filter(d => d.id !== docId));
    } catch (err) { console.error('Delete doc error:', err); alert('Failed to delete'); }
  };

  const saveAccountInfo = async () => {
    setSaving(true); setAccountError(''); setAccountSuccess('');
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API_BASE}/admin/profile`, { name: accountInfo.name, phone: accountInfo.phone, email: accountInfo.email }, { headers });
      setEditingAccount(false);
      setAccountSuccess('Account info updated!');
      setTimeout(() => setAccountSuccess(''), 3000);
    } catch (e) { setAccountError(e.response?.data?.detail || 'Failed to update'); } finally { setSaving(false); }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API_BASE}/admin/profile`, { profile }, { headers });
      setEditingProfile(false);
      setProfileSuccess('Personal info updated!');
      setTimeout(() => setProfileSuccess(''), 3000);
    } catch (e) { console.error('Error saving profile:', e); } finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    setPasswordError(''); setPasswordSuccess('');
    if (passwordData.newPass !== passwordData.confirm) { setPasswordError('Passwords do not match'); return; }
    if (passwordData.newPass.length < 8) { setPasswordError('Min 8 characters required'); return; }
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API_BASE}/admin/change-password`, { current_password: passwordData.current, new_password: passwordData.newPass }, { headers });
      setPasswordSuccess('Password changed!');
      setPasswordData({ current: '', newPass: '', confirm: '' }); setChangingPassword(false);
      setTimeout(() => setPasswordSuccess(''), 3000);
    } catch (e) { setPasswordError(e.response?.data?.detail || 'Failed to change password'); } finally { setSaving(false); }
  };

  const handleChangeEmail = async () => {
    setAccountError('');
    if (!emailData.newEmail || !emailData.password) { setAccountError('Email and password required'); return; }
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API_BASE}/admin/change-email`, { new_email: emailData.newEmail, password: emailData.password }, { headers });
      setAccountSuccess('Email changed! Please login with new email.');
      setEmailData({ newEmail: '', password: '' }); setChangingEmail(false);
    } catch (e) { setAccountError(e.response?.data?.detail || 'Failed to change email'); } finally { setSaving(false); }
  };

  return (
    <div className="profile-section" data-testid="admin-profile-panel">
      {/* Account Information Card */}
      <div className="profile-card">
        <div className="profile-card-header">
          <h3>Account Information</h3>
          {!editingAccount ? (
            <Button onClick={() => { setEditingAccount(true); setAccountError(''); setAccountSuccess(''); }} className="edit-btn" data-testid="admin-edit-info">
              <Edit2 size={16} /> Edit
            </Button>
          ) : (
            <div className="profile-actions">
              <Button onClick={saveAccountInfo} disabled={saving} className="save-btn" data-testid="admin-save-info">{saving ? 'Saving...' : 'Save'}</Button>
              <Button onClick={() => { setEditingAccount(false); fetchAdminProfile(); }} variant="outline" className="cancel-btn">Cancel</Button>
            </div>
          )}
        </div>
        {accountError && <div style={{ padding: '0.75rem 1.5rem' }}><div className="error-banner"><AlertTriangle size={16} /><span>{accountError}</span></div></div>}
        {accountSuccess && <div style={{ padding: '0.75rem 1.5rem' }}><div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', color: '#166534', fontSize: '0.9rem' }}><CheckCircle size={16} /><span>{accountSuccess}</span></div></div>}
        <div className="profile-grid">
          <div className="profile-field">
            <label><User size={16} /> Name</label>
            {editingAccount ? <input type="text" value={accountInfo.name} onChange={(e) => setAccountInfo({...accountInfo, name: e.target.value})} data-testid="admin-name-input" /> : <span>{accountInfo.name || 'Not set'}</span>}
          </div>
          <div className="profile-field">
            <label><Mail size={16} /> Email</label>
            {editingAccount ? <input type="email" value={accountInfo.email} onChange={(e) => setAccountInfo({...accountInfo, email: e.target.value})} data-testid="admin-email-input" /> : <span>{accountInfo.email}</span>}
          </div>
          <div className="profile-field">
            <label><Phone size={16} /> Phone</label>
            {editingAccount ? <input type="tel" value={accountInfo.phone} onChange={(e) => setAccountInfo({...accountInfo, phone: e.target.value})} placeholder="+91 XXXXX XXXXX" data-testid="admin-phone-input" /> : <span>{accountInfo.phone || 'Not set'}</span>}
          </div>
          <div className="profile-field">
            <label><Shield size={16} /> Role</label>
            <span style={{ textTransform: 'capitalize' }}>{currentUser?.role || 'admin'}</span>
          </div>
        </div>
      </div>

      {/* Change Password Card */}
      <div className="profile-card">
        <div className="profile-card-header">
          <h3>Change Password</h3>
          {!changingPassword && <Button onClick={() => { setChangingPassword(true); setPasswordError(''); setPasswordSuccess(''); }} className="edit-btn" data-testid="admin-change-password-btn"><Lock size={16} /> Change</Button>}
        </div>
        {passwordError && <div style={{ padding: '0.75rem 1.5rem' }}><div className="error-banner"><AlertTriangle size={16} /><span>{passwordError}</span></div></div>}
        {passwordSuccess && <div style={{ padding: '0.75rem 1.5rem' }}><div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', color: '#166534', fontSize: '0.9rem' }}><CheckCircle size={16} /><span>{passwordSuccess}</span></div></div>}
        {changingPassword && (
          <div className="profile-grid" style={{ gridTemplateColumns: '1fr' }}>
            <div className="profile-field">
              <label><Lock size={16} /> Current Password</label>
              <div className="password-field-wrapper">
                <input type={showCurrentPwd ? 'text' : 'password'} value={passwordData.current} onChange={(e) => setPasswordData({...passwordData, current: e.target.value})} placeholder="Enter current password" data-testid="admin-current-password" />
                <button type="button" className="password-field-toggle" onClick={() => setShowCurrentPwd(!showCurrentPwd)}>{showCurrentPwd ? <Eye size={16} /> : <EyeOff size={16} />}</button>
              </div>
            </div>
            <div className="profile-field">
              <label><Lock size={16} /> New Password</label>
              <div className="password-field-wrapper">
                <input type={showNewPwd ? 'text' : 'password'} value={passwordData.newPass} onChange={(e) => setPasswordData({...passwordData, newPass: e.target.value})} placeholder="Min 8 characters" data-testid="admin-new-password" />
                <button type="button" className="password-field-toggle" onClick={() => setShowNewPwd(!showNewPwd)}>{showNewPwd ? <Eye size={16} /> : <EyeOff size={16} />}</button>
              </div>
            </div>
            <div className="profile-field">
              <label><Lock size={16} /> Confirm New Password</label>
              <input type="password" value={passwordData.confirm} onChange={(e) => setPasswordData({...passwordData, confirm: e.target.value})} placeholder="Re-enter new password" data-testid="admin-confirm-password" />
            </div>
            <div className="profile-actions" style={{ padding: '0 0 0.5rem', justifyContent: 'flex-start' }}>
              <Button onClick={handleChangePassword} disabled={saving} className="save-btn" data-testid="admin-save-password">{saving ? 'Saving...' : 'Update Password'}</Button>
              <Button onClick={() => { setChangingPassword(false); setPasswordError(''); setPasswordData({ current: '', newPass: '', confirm: '' }); }} variant="outline" className="cancel-btn">Cancel</Button>
            </div>
          </div>
        )}
      </div>

      {/* Personal Information Card */}
      <div className="profile-card">
        <div className="profile-card-header">
          <h3>Personal Information</h3>
          {!editingProfile ? (
            <Button onClick={() => setEditingProfile(true)} className="edit-btn" data-testid="admin-edit-profile"><Edit2 size={16} /> Edit</Button>
          ) : (
            <div className="profile-actions">
              <Button onClick={saveProfile} disabled={saving} className="save-btn" data-testid="admin-save-profile">{saving ? 'Saving...' : 'Save'}</Button>
              <Button onClick={() => setEditingProfile(false)} variant="outline" className="cancel-btn">Cancel</Button>
            </div>
          )}
        </div>
        {profileSuccess && <div style={{ padding: '0.75rem 1.5rem' }}><div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', color: '#166534', fontSize: '0.9rem' }}><CheckCircle size={16} /><span>{profileSuccess}</span></div></div>}
        <div className="profile-grid">
          <div className="profile-field">
            <label><Cake size={16} /> Birthday</label>
            {editingProfile ? <input type="date" value={profile.birthday} onChange={(e) => setProfile({...profile, birthday: e.target.value})} /> : <span>{profile.birthday || 'Not set'}</span>}
          </div>
          <div className="profile-field">
            <label><Heart size={16} /> Anniversary</label>
            {editingProfile ? <input type="date" value={profile.anniversary} onChange={(e) => setProfile({...profile, anniversary: e.target.value})} /> : <span>{profile.anniversary || 'Not set'}</span>}
          </div>
        </div>
      </div>

      {/* Travel Documents Card */}
      <div className="profile-card">
        <div className="profile-card-header">
          <h3>Travel Documents</h3>
          {!editingProfile && <Button onClick={() => setEditingProfile(true)} className="edit-btn"><Edit2 size={16} /> Edit</Button>}
        </div>
        <div className="profile-grid">
          <div className="profile-field">
            <label><CreditCard size={16} /> Aadhar Number</label>
            {editingProfile ? <input type="text" value={profile.aadhar_number} onChange={(e) => setProfile({...profile, aadhar_number: e.target.value})} placeholder="XXXX XXXX XXXX" maxLength="14" /> : <span>{profile.aadhar_number ? `****${profile.aadhar_number.slice(-4)}` : 'Not set'}</span>}
          </div>
          <div className="profile-field">
            <label><CreditCard size={16} /> PAN Number</label>
            {editingProfile ? <input type="text" value={profile.pan_number} onChange={(e) => setProfile({...profile, pan_number: e.target.value.toUpperCase()})} placeholder="ABCDE1234F" maxLength="10" /> : <span>{profile.pan_number ? `${profile.pan_number.slice(0,3)}****${profile.pan_number.slice(-2)}` : 'Not set'}</span>}
          </div>
          <div className="profile-field">
            <label><FileText size={16} /> Passport Number</label>
            {editingProfile ? <input type="text" value={profile.passport_number} onChange={(e) => setProfile({...profile, passport_number: e.target.value.toUpperCase()})} placeholder="A1234567" maxLength="10" /> : <span>{profile.passport_number ? `****${profile.passport_number.slice(-4)}` : 'Not set'}</span>}
          </div>
          <div className="profile-field">
            <label><Calendar size={16} /> Passport Expiry</label>
            {editingProfile ? <input type="date" value={profile.passport_expiry} onChange={(e) => setProfile({...profile, passport_expiry: e.target.value})} /> : <span>{profile.passport_expiry || 'Not set'}</span>}
          </div>
        </div>
      </div>

      {/* Upload Documents Card */}
      <div className="profile-card">
        <div className="profile-card-header">
          <h3>Upload Documents</h3>
          <select
            className="doc-type-select"
            onChange={(e) => {
              if (e.target.value) {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = '.jpg,.jpeg,.png,.pdf';
                input.onchange = (ev) => handleDocUpload(ev, e.target.value);
                input.click();
                e.target.value = '';
              }
            }}
            disabled={uploadingDoc}
            data-testid="admin-upload-doc-select"
          >
            <option value="">+ Upload Document</option>
            <option value="aadhar">Aadhar Card</option>
            <option value="pan">PAN Card</option>
            <option value="passport">Passport</option>
            <option value="visa">Visa</option>
            <option value="other">Other</option>
          </select>
        </div>

        {uploadMessage && (
          <div style={{ padding: '0.75rem 1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', color: '#166534', fontSize: '0.9rem' }}>
              <CheckCircle size={16} /><span>{uploadMessage}</span>
            </div>
          </div>
        )}
        {uploadError && (
          <div style={{ padding: '0.75rem 1.5rem' }}>
            <div className="error-banner"><AlertTriangle size={16} /><span>{uploadError}</span></div>
          </div>
        )}

        <div className="documents-list">
          {documents.length === 0 ? (
            <p className="no-docs" style={{ padding: '1rem 1.5rem', color: '#718096' }}>No documents uploaded yet.</p>
          ) : (
            documents.map((doc) => (
              <div key={doc.id} className="document-item" data-testid={`admin-doc-${doc.id}`}>
                <div className="doc-info">
                  <FileText size={20} />
                  <div>
                    <span className="doc-name">{doc.name || 'Document'}</span>
                    <span className="doc-type">{(doc.doc_type || '').charAt(0).toUpperCase() + (doc.doc_type || '').slice(1)}</span>
                  </div>
                </div>
                <div className="doc-actions">
                  <a href={`${API_BASE}/${doc.url?.replace('/api/', '')}`} target="_blank" rel="noopener noreferrer" className="view-doc-btn" data-testid={`admin-view-doc-${doc.id}`}>
                    <Eye size={16} />
                  </a>
                  <button onClick={() => handleDeleteDoc(doc.id)} className="delete-doc-btn" data-testid={`admin-delete-doc-${doc.id}`}>
                    <X size={16} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// Settings Panel Component

const SettingsPanel = ({ token, seedData, currentUser }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [storageStats, setStorageStats] = useState(null);
  const [storageSettings, setStorageSettings] = useState({
    auto_clear_enabled: true,
    auto_clear_time: '00:00',
    clear_cache_days: 1,
    clear_temp_days: 1,
    clear_exports_days: 7
  });
  const [clearing, setClearing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dataCounts, setDataCounts] = useState(null);
  const [userDocuments, setUserDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);

  useEffect(() => {
    fetchStorageStats();
    fetchStorageSettings();
    fetchDataCounts();
    fetchUserDocuments();
  }, []);

  const fetchDataCounts = async () => {
    try {
      const response = await axios.get(`${API}/admin/data/counts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDataCounts(response.data);
    } catch (error) {
      console.error('Error fetching data counts:', error);
    }
  };

  const fetchUserDocuments = async () => {
    setLoadingDocs(true);
    try {
      const response = await axios.get(`${API}/admin/user-documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserDocuments(response.data || []);
    } catch (error) {
      console.error('Error fetching user documents:', error);
    } finally { setLoadingDocs(false); }
  };

  const fetchStorageStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/storage/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStorageStats(response.data);
    } catch (error) {
      console.error('Error fetching storage stats:', error);
    }
  };

  const fetchStorageSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/storage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStorageSettings(response.data);
    } catch (error) {
      console.error('Error fetching storage settings:', error);
    }
  };

  const handleClearCache = async (clearType = 'all') => {
    if (!window.confirm(`Are you sure you want to clear ${clearType === 'all' ? 'all cache and temporary files' : clearType}?`)) return;
    
    setClearing(true);
    try {
      const response = await axios.post(`${API}/admin/storage/clear-cache?clear_type=${clearType}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: response.data.message });
      fetchStorageStats();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to clear cache' });
    } finally {
      setClearing(false);
    }
  };

  const handleSaveStorageSettings = async () => {
    setSaving(true);
    try {
      const params = new URLSearchParams({
        auto_clear_enabled: storageSettings.auto_clear_enabled,
        auto_clear_time: storageSettings.auto_clear_time,
        clear_cache_days: storageSettings.clear_cache_days,
        clear_temp_days: storageSettings.clear_temp_days,
        clear_exports_days: storageSettings.clear_exports_days
      });
      await axios.put(`${API}/admin/settings/storage?${params}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'Storage settings saved successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save storage settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async (type, format) => {
    setExporting(true);
    try {
      let endpoint;
      if (type === 'leads') {
        endpoint = '/admin/export/leads';
      } else if (type === 'inquiries') {
        endpoint = '/admin/export/inquiries';
      } else if (type === 'users') {
        endpoint = '/admin/export/users';
      }
      
      const response = await axios.get(`${API}${endpoint}?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      let filename;
      if (type === 'leads') {
        filename = `JNT_Leads_Export.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      } else if (type === 'inquiries') {
        filename = `JNT_Inquiries_Export.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      } else if (type === 'users') {
        filename = `JNT_Users_Export.xlsx`;
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setMessage({ type: 'success', text: `${type} exported successfully` });
    } catch (error) {
      setMessage({ type: 'error', text: `Failed to export ${type}` });
    } finally {
      setExporting(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/admin/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'Password changed successfully' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to change password' });
    } finally {
      setSaving(false);
    }
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!newEmail || !newEmail.includes('@')) {
      setMessage({ type: 'error', text: 'Please enter a valid email' });
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/admin/change-email`, {
        new_email: newEmail,
        password: currentPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'Email changed successfully. Please login with your new email.' });
      setNewEmail('');
      setCurrentPassword('');
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to change email' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="settings-content">
      {message.text && (
        <div className={`settings-message ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Unified Data & Storage Management */}
      <div className="settings-card">
        <h3><Database size={18} /> Data & Storage Management</h3>
        
        {/* Data Overview */}
        <h4 style={{ marginTop: '0.5rem', marginBottom: '0.75rem', color: '#4a5568', fontSize: '0.95rem' }}>Data Overview</h4>
        {dataCounts && (
          <div className="storage-stats" style={{ marginBottom: '1rem' }}>
            <div className="stat-item"><span className="stat-label">Itinerary Leads</span><span className="stat-value">{dataCounts.itinerary_leads || 0} records</span></div>
            <div className="stat-item"><span className="stat-label">Inquiries</span><span className="stat-value">{dataCounts.inquiries || 0} records</span></div>
            <div className="stat-item"><span className="stat-label">Users (Clients/Partners/Staff)</span><span className="stat-value">{dataCounts.users || 0} records</span></div>
            <div className="stat-item"><span className="stat-label">Experiences</span><span className="stat-value">{dataCounts.experiences || 0} records</span></div>
            <div className="stat-item"><span className="stat-label">Destinations</span><span className="stat-value">{dataCounts.destinations || 0} records</span></div>
            <div className="stat-item"><span className="stat-label">Testimonials</span><span className="stat-value">{dataCounts.testimonials || 0} records</span></div>
            <div className="stat-item"><span className="stat-label">JNT Tales (Blog Posts)</span><span className="stat-value">{dataCounts.blog_posts || 0} records</span></div>
            <div className="stat-item"><span className="stat-label">Gallery Items</span><span className="stat-value">{dataCounts.gallery_items || 0} records</span></div>
          </div>
        )}

        {/* Export Section */}
        <h4 style={{ marginTop: '0.5rem', marginBottom: '0.75rem', color: '#4a5568', fontSize: '0.95rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem' }}>Export Data</h4>
        <div className="export-section">
          <div className="export-group">
            <h4>Itinerary Leads</h4>
            <div className="export-buttons">
              <Button onClick={() => handleExport('leads', 'excel')} disabled={exporting} className="export-btn excel">
                <FileText size={16} /> Excel
              </Button>
              <Button onClick={() => handleExport('leads', 'pdf')} disabled={exporting} className="export-btn pdf">
                <FileText size={16} /> PDF
              </Button>
            </div>
          </div>
          <div className="export-group">
            <h4>General Inquiries</h4>
            <div className="export-buttons">
              <Button onClick={() => handleExport('inquiries', 'excel')} disabled={exporting} className="export-btn excel">
                <FileText size={16} /> Excel
              </Button>
              <Button onClick={() => handleExport('inquiries', 'pdf')} disabled={exporting} className="export-btn pdf">
                <FileText size={16} /> PDF
              </Button>
            </div>
          </div>
          <div className="export-group">
            <h4>User Data</h4>
            <p className="export-hint">Export all registered users (clients, partners, staff)</p>
            <div className="export-buttons">
              <Button onClick={() => handleExport('users', 'excel')} disabled={exporting} className="export-btn excel">
                <Users size={16} /> Export Users (Excel)
              </Button>
            </div>
          </div>
        </div>

        {/* Storage Stats */}
        <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', color: '#4a5568', fontSize: '0.95rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem' }}>Storage</h4>
        {storageStats && (
          <div className="storage-stats">
            <div className="stat-item">
              <span className="stat-label">Itineraries PDFs</span>
              <span className="stat-value">{storageStats.itineraries?.size_mb || 0} MB ({storageStats.itineraries?.file_count || 0} files)</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Exports</span>
              <span className="stat-value">{storageStats.exports?.size_mb || 0} MB ({storageStats.exports?.file_count || 0} files)</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Cache</span>
              <span className="stat-value">{storageStats.cache?.size_mb || 0} MB ({storageStats.cache?.file_count || 0} files)</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Temp Files</span>
              <span className="stat-value">{storageStats.temp?.size_mb || 0} MB ({storageStats.temp?.file_count || 0} files)</span>
            </div>
            <div className="stat-item total">
              <span className="stat-label">Total Storage Used</span>
              <span className="stat-value">{storageStats.total_storage_mb || 0} MB</span>
            </div>
          </div>
        )}
        <div className="clear-buttons">
          <Button onClick={() => handleClearCache('cache')} disabled={clearing} className="clear-btn">Clear Cache</Button>
          <Button onClick={() => handleClearCache('temp')} disabled={clearing} className="clear-btn">Clear Temp</Button>
          <Button onClick={() => handleClearCache('exports')} disabled={clearing} className="clear-btn">Clear Old Exports</Button>
          <Button onClick={() => handleClearCache('all')} disabled={clearing} className="clear-btn danger">{clearing ? 'Clearing...' : 'Clear All'}</Button>
        </div>
        <Button onClick={fetchStorageStats} className="refresh-storage-btn" style={{ marginTop: '0.5rem' }}>
          <RefreshCw size={16} /> Refresh Stats
        </Button>

        {/* Seed Data */}
        <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', color: '#4a5568', fontSize: '0.95rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem' }}>Content Management</h4>
        <Button onClick={seedData} className="seed-btn">
          <RefreshCw size={18} />
          Re-initialize Default Content
        </Button>
        <p className="settings-note">This will add default destinations and experiences if they don't exist.</p>

        {/* User Documents */}
        <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', color: '#4a5568', fontSize: '0.95rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem' }}>User Uploaded Documents ({userDocuments.length})</h4>
        {loadingDocs ? (
          <p style={{ color: '#718096', fontSize: '0.9rem' }}>Loading documents...</p>
        ) : userDocuments.length === 0 ? (
          <p style={{ color: '#a0aec0', fontSize: '0.9rem' }}>No user documents uploaded yet.</p>
        ) : (
          <div className="user-docs-storage-list" data-testid="storage-user-documents">
            {userDocuments.map((doc, idx) => (
              <div key={doc.id || idx} className="storage-doc-item">
                <div className="storage-doc-info">
                  <FileText size={16} />
                  <div>
                    <span className="storage-doc-name">{doc.name || 'Document'}</span>
                    <span className="storage-doc-meta">{doc.doc_type} &middot; {doc.user_name} ({doc.user_email})</span>
                  </div>
                </div>
                <a 
                  href={doc.url?.startsWith('http') ? doc.url : `${API}/${doc.url?.replace('/api/', '')}`} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="storage-doc-download"
                  data-testid={`storage-download-doc-${doc.id || idx}`}
                >
                  <Download size={14} /> Download
                </a>
              </div>
            ))}
          </div>
        )}
        <Button onClick={fetchUserDocuments} className="refresh-storage-btn" style={{ marginTop: '0.5rem' }}>
          <RefreshCw size={16} /> Refresh Documents
        </Button>
      </div>

      {/* User Management */}
      <AdminErrorBoundary name="User Management">
      <UserManagementPanel token={token} currentUser={currentUser} />
      </AdminErrorBoundary>

      {/* Storage Capacity Configuration */}
      <AdminErrorBoundary name="Storage Capacity">
      <StorageCapacityPanel token={token} />
      </AdminErrorBoundary>

      {/* Website Health Check */}
      <AdminErrorBoundary name="Health Check">
      <HealthCheckPanel token={token} />
      </AdminErrorBoundary>
    </div>
  );
};

// Experience Form Component

export { StorageCapacityPanel, FormFieldEditor, AdminProfilePanel, SettingsPanel };
